import { isAPIEvent } from 'chessboard/core/model/is-api-event';
import type { Core } from 'chessboard/core/config/types';
import { ChessError } from 'chessboard/model/error';
import { ErrorCodes } from 'chessboard/model/error/types';
import { shouldIgnoreEvent } from 'chessboard/core/state/should-ignore-event';
import { stateMachine } from 'chessboard/core/state/machine';
import { StateMachine } from 'chessboard/core/state/config/types';
import { Timer } from 'chessboard/plugins/clocks/timer/config/types';

/**
 * Ex "Core Plugin".
 *
 * Creates state for the user/API events,
 * which could be used by plugins or renderer.
 *
 * The state machine maintains its own board state (params),
 * e.g. idle, dragging, selected, promotionWindowOpen, and transitions between
 * those states according to events.
 */
export function createUpdateMachineState() {
  const params = stateMachine.getParams();
  const UpdateParams = stateMachine.createUpdateParams(params);
  let currentState = stateMachine.getCurrentState();

  const statelessEvents = [Timer.Events.Tick];

  return function updateMachineState(event: Core.Event, context: Core.Context) {
    if (statelessEvents.includes(event.type)) {
      return;
    }

    const { state } = context;

    state.set({
      draggingPiece: params.draggingPiece,
      machineState: currentState,
      promotionMove: params.promotionMove,
      selectedPiece: params.selectedPiece,
    });

    const eventType =
      isAPIEvent(event) && !shouldIgnoreEvent(event.type) ? 'anyAPIEvent' : event.type;

    if (!stateMachine.states[currentState] || !stateMachine.states[currentState][eventType]) {
      state.set({ machineState: currentState });

      return;
    }

    // Pass the event through the machine, getting the next state and commands
    const nextState = stateMachine.states[currentState][eventType]({
      context,
      data: event.data,
      params,
      UpdateParams,
      type: event.type,
    });

    // Update the machine state
    const oldState = currentState;
    currentState = nextState || currentState;

    if (oldState !== currentState) {
      // fix for drag cursor on safari
      if (currentState === StateMachine.States.PieceDragging) {
        document.onselectstart = () => false;
      } else if (oldState === StateMachine.States.PieceDragging) {
        document.onselectstart = null;
      }
    }

    if (!machineIsValid()) {
      // eslint-disable-next-line no-throw-literal
      throw {
        data: {
          draggingPiece: params.draggingPiece,
          machineState: currentState,
          oldState,
          selectedPiece: params.selectedPiece,
        },
        error: new ChessError({
          code: ErrorCodes.InvalidStateMachine,
          message: 'The machine parameters and state do not match up.',
        }),
      };
    }

    // This is required for the move hints plugin.
    state.set({
      draggingPiece: params.draggingPiece,
      machineState: currentState,
      selectedPiece: params.selectedPiece,
    });
  };

  /**
   * Machine is valid.
   */
  function machineIsValid(): boolean {
    if (currentState === StateMachine.States.Idle) {
      return !params.draggingPiece && !params.selectedPiece;
    }

    if (currentState === StateMachine.States.PieceDragging) {
      return Boolean(params.draggingPiece);
    }

    if (currentState === StateMachine.States.PieceSelected) {
      return !params.draggingPiece && Boolean(params.selectedPiece);
    }

    return true;
  }
}
